import type { Entity, PlayerForm } from '../components'
import type { Camera } from '../../engine/camera'
import type { Renderer } from '../../engine/renderer'
import type { GameWorld } from '../world'
import { loadShader } from '../../engine/shader-loader'
import { activeRipples, MAX_RIPPLES, particleKindId } from './particles'
import { tierMorphProgress, tierFlourish, formGrowth, growthAt, type Tier } from '../evolution'
import { FORM_ID, formRevealValue } from './lineage'
import { powerLevel } from '../powers'
import { BIOMES, biomeBackgroundAt } from '../biomes'
import { getActiveDungeon } from '../dungeon/active'
import { T } from '../tunables'

export interface ExtraPlayerSpec {
  x: number
  y: number
  radius: number
  phase: number
  tier?: number
  mutationFlags: number
  mutationStacks: number
  // Species-review previews (gallery): force a specific critter form at a
  // chosen growth (0..1), independent of the live player's lineage. When
  // unset, a preview inherits the player's resolved form (mutation row).
  form?: PlayerForm
  growth?: number
}

export interface RenderSystem {
  draw(timeSec: number): void
  extraPlayers: ExtraPlayerSpec[]
}

const MAX_MICROBES = 1024
const MAX_HAZARDS = 32
const MAX_LANDMARKS = 8
const MAX_EYES = 128
const MAX_THREADS = 96
const MAX_PARTICLES = 256
// creature.slang Instance holds a float3 tint (WGSL align 16), so the
// StructuredBuffer element stride rounds UP to a multiple of 16. 20 floats =
// 80 bytes (a multiple of 16). A mismatch desyncs every instance after the
// first (48→56 rounded to 64 on the GPU and flooded the scene — 2026-07-03).
// Payload: pos2+radius+phase+tint3+variant+featureFlags+seed+featureA+featureB+
// heading+speedN+stateF+featureFlags2+featureC+featureD+pad2.
const INSTANCE_STRIDE = 80
// 18 floats = 72 bytes (multiple of 8 — Instance holds a float2, so WGSL
// rounds the array stride to 8; a mismatch desyncs every instance after
// the first). Payload: pos2+radius+phase+vel2+shield+tier+mutFlags+
// mutStacks+redMix+horns+tail+lashAngle+lashLenUV+lashProg+reachUV+engulf.
const PLAYER_INSTANCE_STRIDE = 72
const CHAIN_INSTANCE_STRIDE = 32  // matches chain.slang Instance (8 floats: pos2+radius+phase+hueOffset+hitFlash+awakeFrac+pad)
const HAZARD_INSTANCE_STRIDE = 48 // 12 floats: pos(2)+radius(1)+phase(1)+tint(3)+shapeId(1)+impulseDir(2)+pad(2)
const LANDMARK_INSTANCE_STRIDE = 32 // 8 floats: pos(2)+radius(1)+phase(1)+tint(3)+shapeId(1)
const EYES_INSTANCE_STRIDE = 32   // matches eyes.slang Instance: pos(2)+socketR(1)+pupilR(1)+pupilOff(2)+pad(2)
const HYPHAE_INSTANCE_STRIDE = 32 // matches hyphae.slang Instance: a(2)+b(2)+thickness(1)+alpha(1)+pad(2)
const PARTICLE_INSTANCE_STRIDE = 32 // 8 floats: pos(2)+size(1)+ageFrac(1)+tint(3)+kind(1)
const SCENE_UNIFORM_SIZE = 96     // 4×4 matrix (64) + float (4) + pad (28) — std140
const POST_UNIFORM_SIZE = 16 + 32 * 8 // float4 screen + 8 ripples × 32 bytes

// Dungeon wall primitives (walls.slang): float4 slots — discs (x,y,r,0),
// then capsules as two float4s each. Only primitives near the viewport are
// uploaded (the world region carries ~230 slots total; per-pixel smin over
// all of them would tax integrated GPUs for geometry that isn't on screen).
const MAX_WALL_F4 = 512
// World-unit margin around the visible rect. Covers the smin rounding
// (T.dungeon.sminK) and the shader's edge wobble so culling never pops a
// wall edge mid-screen.
const WALL_CULL_MARGIN = 2.5

const HAZARD_SHAPE_ID: Record<string, number> = { spike: 0, current: 1, toxic: 2, spore: 3, portal: 4 }
const LANDMARK_SHAPE_ID: Record<string, number> = { reticle: 0, droplet: 1, duck: 2 }

const PREMUL_ALPHA_BLEND: GPUBlendState = {
  color: { srcFactor: 'one', dstFactor: 'one-minus-src-alpha', operation: 'add' },
  alpha: { srcFactor: 'one', dstFactor: 'one-minus-src-alpha', operation: 'add' },
}

function createOffscreen(device: GPUDevice, w: number, h: number): GPUTexture {
  return device.createTexture({
    size: { width: Math.max(1, w), height: Math.max(1, h) },
    format: 'rgba16float',
    usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
    label: 'offscreen',
  })
}

function buildPostBindGroup(
  device: GPUDevice,
  layout: GPUBindGroupLayout,
  uniformBuf: GPUBuffer,
  textureView: GPUTextureView,
  sampler: GPUSampler,
): GPUBindGroup {
  return device.createBindGroup({
    layout,
    entries: [
      { binding: 0, resource: { buffer: uniformBuf } },
      { binding: 1, resource: textureView },
      { binding: 2, resource: sampler },
    ],
    label: 'post-bind-group',
  })
}

export async function createRenderSystem(
  renderer: Renderer,
  world: GameWorld,
  camera: Camera,
  playerRef: () => Entity,
): Promise<RenderSystem> {
  const { device, format } = renderer

  // ── offscreen HDR texture ──────────────────────────────────────────────────
  let offscreen = createOffscreen(device, renderer.width, renderer.height)
  let offscreenView = offscreen.createView()

  // ── background pipeline ────────────────────────────────────────────────────
  const bgModule = device.createShaderModule({ code: loadShader('background'), label: 'background' })

  // Background uniforms: 32 bytes camera+misc + 8 halos × 16 (160) + wash/ink
  // float4×2 (32) = 192 bytes. floaties + hub-ring use the smaller 32-byte
  // layout via separate buffers.
  const bgUniformBuf = device.createBuffer({
    size: 192,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    label: 'bg-uniforms',
  })

  const bgBGL = device.createBindGroupLayout({
    label: 'bg-bgl',
    entries: [{
      binding: 0,
      visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
      buffer: { type: 'uniform' },
    }],
  })

  const bgBindGroup = device.createBindGroup({
    layout: bgBGL,
    entries: [{ binding: 0, resource: { buffer: bgUniformBuf } }],
    label: 'bg-bg',
  })

  const bgPipeline = device.createRenderPipeline({
    label: 'bg-pipeline',
    layout: device.createPipelineLayout({ bindGroupLayouts: [bgBGL] }),
    vertex: { module: bgModule, entryPoint: 'vsMain' },
    fragment: {
      module: bgModule,
      entryPoint: 'fsMain',
      targets: [{ format: 'rgba16float', blend: PREMUL_ALPHA_BLEND }],
    },
    primitive: { topology: 'triangle-strip' },
  })

  // ── microbe (creature) pipeline ────────────────────────────────────────────
  const creatureModule = device.createShaderModule({ code: loadShader('creature'), label: 'creature' })

  const creatureUniformBuf = device.createBuffer({
    size: SCENE_UNIFORM_SIZE,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    label: 'creature-uniforms',
  })

  const creatureInstanceBuf = device.createBuffer({
    size: INSTANCE_STRIDE * MAX_MICROBES,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    label: 'creature-instances',
  })

  const creatureBGL = device.createBindGroupLayout({
    label: 'creature-bgl',
    entries: [
      { binding: 0, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
      { binding: 1, visibility: GPUShaderStage.VERTEX, buffer: { type: 'read-only-storage' } },
    ],
  })

  const creatureBindGroup = device.createBindGroup({
    layout: creatureBGL,
    entries: [
      { binding: 0, resource: { buffer: creatureUniformBuf } },
      { binding: 1, resource: { buffer: creatureInstanceBuf } },
    ],
    label: 'creature-bg',
  })

  const creaturePipeline = device.createRenderPipeline({
    label: 'creature-pipeline',
    layout: device.createPipelineLayout({ bindGroupLayouts: [creatureBGL] }),
    vertex: { module: creatureModule, entryPoint: 'vsMain' },
    fragment: {
      module: creatureModule,
      entryPoint: 'fsMain',
      targets: [{ format: 'rgba16float', blend: PREMUL_ALPHA_BLEND }],
    },
    primitive: { topology: 'triangle-strip' },
  })

  // ── player pipeline ────────────────────────────────────────────────────────
  const playerModule = device.createShaderModule({ code: loadShader('player'), label: 'player' })

  const playerUniformBuf = device.createBuffer({
    size: SCENE_UNIFORM_SIZE,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    label: 'player-uniforms',
  })

  const MAX_PLAYER_INSTANCES = 16
  const playerInstanceBuf = device.createBuffer({
    size: MAX_PLAYER_INSTANCES * PLAYER_INSTANCE_STRIDE,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    label: 'player-instance',
  })

  const playerBGL = device.createBindGroupLayout({
    label: 'player-bgl',
    entries: [
      { binding: 0, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
      { binding: 1, visibility: GPUShaderStage.VERTEX, buffer: { type: 'read-only-storage' } },
    ],
  })

  const playerBindGroup = device.createBindGroup({
    layout: playerBGL,
    entries: [
      { binding: 0, resource: { buffer: playerUniformBuf } },
      { binding: 1, resource: { buffer: playerInstanceBuf } },
    ],
    label: 'player-bg',
  })

  const playerPipeline = device.createRenderPipeline({
    label: 'player-pipeline',
    layout: device.createPipelineLayout({ bindGroupLayouts: [playerBGL] }),
    vertex: { module: playerModule, entryPoint: 'vsMain' },
    fragment: {
      module: playerModule,
      entryPoint: 'fsMain',
      targets: [{ format: 'rgba16float', blend: PREMUL_ALPHA_BLEND }],
    },
    primitive: { topology: 'triangle-strip' },
  })

  // ── hazard pipeline ────────────────────────────────────────────────────────
  const hazardModule = device.createShaderModule({ code: loadShader('hazard'), label: 'hazard' })

  const hazardUniformBuf = device.createBuffer({
    size: SCENE_UNIFORM_SIZE,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    label: 'hazard-uniforms',
  })

  const hazardInstanceBuf = device.createBuffer({
    size: HAZARD_INSTANCE_STRIDE * MAX_HAZARDS,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    label: 'hazard-instances',
  })

  const hazardBGL = device.createBindGroupLayout({
    label: 'hazard-bgl',
    entries: [
      { binding: 0, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
      { binding: 1, visibility: GPUShaderStage.VERTEX, buffer: { type: 'read-only-storage' } },
    ],
  })

  const hazardBindGroup = device.createBindGroup({
    layout: hazardBGL,
    entries: [
      { binding: 0, resource: { buffer: hazardUniformBuf } },
      { binding: 1, resource: { buffer: hazardInstanceBuf } },
    ],
    label: 'hazard-bg',
  })

  const hazardPipeline = device.createRenderPipeline({
    label: 'hazard-pipeline',
    layout: device.createPipelineLayout({ bindGroupLayouts: [hazardBGL] }),
    vertex: { module: hazardModule, entryPoint: 'vsMain' },
    fragment: {
      module: hazardModule,
      entryPoint: 'fsMain',
      targets: [{ format: 'rgba16float', blend: PREMUL_ALPHA_BLEND }],
    },
    primitive: { topology: 'triangle-strip' },
  })

  // ── chain boss pipeline ────────────────────────────────────────────────────
  const chainModule = device.createShaderModule({ code: loadShader('chain'), label: 'chain' })

  const chainUniformBuf = device.createBuffer({
    size: SCENE_UNIFORM_SIZE,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    label: 'chain-uniforms',
  })

  const MAX_CHAIN_INSTANCES = 8  // up to 7 segments + 1 gallery preview
  const chainInstanceBuf = device.createBuffer({
    size: MAX_CHAIN_INSTANCES * CHAIN_INSTANCE_STRIDE,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    label: 'chain-instances',
  })

  const chainBGL = device.createBindGroupLayout({
    label: 'chain-bgl',
    entries: [
      { binding: 0, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
      { binding: 1, visibility: GPUShaderStage.VERTEX, buffer: { type: 'read-only-storage' } },
    ],
  })

  const chainBindGroup = device.createBindGroup({
    layout: chainBGL,
    entries: [
      { binding: 0, resource: { buffer: chainUniformBuf } },
      { binding: 1, resource: { buffer: chainInstanceBuf } },
    ],
    label: 'chain-bg',
  })

  const chainPipeline = device.createRenderPipeline({
    label: 'chain-pipeline',
    layout: device.createPipelineLayout({ bindGroupLayouts: [chainBGL] }),
    vertex: { module: chainModule, entryPoint: 'vsMain' },
    fragment: {
      module: chainModule,
      entryPoint: 'fsMain',
      targets: [{ format: 'rgba16float', blend: PREMUL_ALPHA_BLEND }],
    },
    primitive: { topology: 'triangle-strip' },
  })

  // ── eyes pipeline (instanced, drawn over all creatures) ──────────────────
  const eyesModule = device.createShaderModule({ code: loadShader('eyes'), label: 'eyes' })

  const eyesUniformBuf = device.createBuffer({
    size: SCENE_UNIFORM_SIZE,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    label: 'eyes-uniforms',
  })

  const eyesInstanceBuf = device.createBuffer({
    size: EYES_INSTANCE_STRIDE * MAX_EYES,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    label: 'eyes-instances',
  })

  const eyesBGL = device.createBindGroupLayout({
    label: 'eyes-bgl',
    entries: [
      { binding: 0, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
      { binding: 1, visibility: GPUShaderStage.VERTEX, buffer: { type: 'read-only-storage' } },
    ],
  })

  const eyesBindGroup = device.createBindGroup({
    layout: eyesBGL,
    entries: [
      { binding: 0, resource: { buffer: eyesUniformBuf } },
      { binding: 1, resource: { buffer: eyesInstanceBuf } },
    ],
    label: 'eyes-bg',
  })

  const eyesPipeline = device.createRenderPipeline({
    label: 'eyes-pipeline',
    layout: device.createPipelineLayout({ bindGroupLayouts: [eyesBGL] }),
    vertex: { module: eyesModule, entryPoint: 'vsMain' },
    fragment: {
      module: eyesModule,
      entryPoint: 'fsMain',
      targets: [{ format: 'rgba16float', blend: PREMUL_ALPHA_BLEND }],
    },
    primitive: { topology: 'triangle-strip' },
  })

  // ── hyphae threads pipeline (mycelial-network strands, drawn under nodes) ──
  const hyphaeModule = device.createShaderModule({ code: loadShader('hyphae'), label: 'hyphae' })

  const hyphaeUniformBuf = device.createBuffer({
    size: SCENE_UNIFORM_SIZE,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    label: 'hyphae-uniforms',
  })

  const hyphaeInstanceBuf = device.createBuffer({
    size: HYPHAE_INSTANCE_STRIDE * MAX_THREADS,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    label: 'hyphae-instances',
  })

  const hyphaeBGL = device.createBindGroupLayout({
    label: 'hyphae-bgl',
    entries: [
      { binding: 0, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
      { binding: 1, visibility: GPUShaderStage.VERTEX, buffer: { type: 'read-only-storage' } },
    ],
  })

  const hyphaeBindGroup = device.createBindGroup({
    layout: hyphaeBGL,
    entries: [
      { binding: 0, resource: { buffer: hyphaeUniformBuf } },
      { binding: 1, resource: { buffer: hyphaeInstanceBuf } },
    ],
    label: 'hyphae-bg',
  })

  const hyphaePipeline = device.createRenderPipeline({
    label: 'hyphae-pipeline',
    layout: device.createPipelineLayout({ bindGroupLayouts: [hyphaeBGL] }),
    vertex: { module: hyphaeModule, entryPoint: 'vsMain' },
    fragment: {
      module: hyphaeModule,
      entryPoint: 'fsMain',
      targets: [{ format: 'rgba16float', blend: PREMUL_ALPHA_BLEND }],
    },
    primitive: { topology: 'triangle-strip' },
  })

  // ── floaties (foreground parallax) pipeline ───────────────────────────────
  const floatiesModule = device.createShaderModule({ code: loadShader('floaties'), label: 'floaties' })

  const floatiesUniformBuf = device.createBuffer({
    size: 32,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    label: 'floaties-uniforms',
  })

  const floatiesBindGroup = device.createBindGroup({
    layout: bgBGL,
    entries: [{ binding: 0, resource: { buffer: floatiesUniformBuf } }],
    label: 'floaties-bg',
  })

  const floatiesPipeline = device.createRenderPipeline({
    label: 'floaties-pipeline',
    layout: device.createPipelineLayout({ bindGroupLayouts: [bgBGL] }),
    vertex: { module: floatiesModule, entryPoint: 'vsMain' },
    fragment: {
      module: floatiesModule,
      entryPoint: 'fsMain',
      targets: [{ format: 'rgba16float', blend: PREMUL_ALPHA_BLEND }],
    },
    primitive: { topology: 'triangle-strip' },
  })

  // ── dungeon walls pipeline (fullscreen SDF over a primitive buffer) ───────
  const wallsModule = device.createShaderModule({ code: loadShader('walls'), label: 'walls' })

  const wallsUniformBuf = device.createBuffer({
    size: 48, // float4 camera + float4 misc + float4 washCol
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    label: 'walls-uniforms',
  })

  const wallsPrimBuf = device.createBuffer({
    size: MAX_WALL_F4 * 16,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    label: 'walls-prims',
  })

  const wallsBGL = device.createBindGroupLayout({
    label: 'walls-bgl',
    entries: [
      { binding: 0, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
      { binding: 1, visibility: GPUShaderStage.FRAGMENT, buffer: { type: 'read-only-storage' } },
    ],
  })

  const wallsBindGroup = device.createBindGroup({
    layout: wallsBGL,
    entries: [
      { binding: 0, resource: { buffer: wallsUniformBuf } },
      { binding: 1, resource: { buffer: wallsPrimBuf } },
    ],
    label: 'walls-bg',
  })

  const wallsPipeline = device.createRenderPipeline({
    label: 'walls-pipeline',
    layout: device.createPipelineLayout({ bindGroupLayouts: [wallsBGL] }),
    vertex: { module: wallsModule, entryPoint: 'vsMain' },
    fragment: {
      module: wallsModule,
      entryPoint: 'fsMain',
      targets: [{ format: 'rgba16float', blend: PREMUL_ALPHA_BLEND }],
    },
    primitive: { topology: 'triangle-strip' },
  })

  // Repacked per frame from the viewport-culled primitive set — small and
  // in line with how every instance buffer here already works.
  const wallsPrimData = new Float32Array(MAX_WALL_F4 * 4)
  let wallsDiscCount = 0
  let wallsCapsuleCount = 0

  // ── hub-ring pipeline (reuses bgBGL: float4 camera + float4 misc) ─────────
  const hubRingModule = device.createShaderModule({ code: loadShader('hub-ring'), label: 'hub-ring' })

  const hubRingUniformBuf = device.createBuffer({
    size: 32,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    label: 'hub-ring-uniforms',
  })

  const hubRingBindGroup = device.createBindGroup({
    layout: bgBGL,
    entries: [{ binding: 0, resource: { buffer: hubRingUniformBuf } }],
    label: 'hub-ring-bg',
  })

  const hubRingPipeline = device.createRenderPipeline({
    label: 'hub-ring-pipeline',
    layout: device.createPipelineLayout({ bindGroupLayouts: [bgBGL] }),
    vertex: { module: hubRingModule, entryPoint: 'vsMain' },
    fragment: {
      module: hubRingModule,
      entryPoint: 'fsMain',
      targets: [{ format: 'rgba16float', blend: PREMUL_ALPHA_BLEND }],
    },
    primitive: { topology: 'triangle-strip' },
  })

  // ── landmark pipeline ──────────────────────────────────────────────────────
  const landmarkModule = device.createShaderModule({ code: loadShader('landmark'), label: 'landmark' })

  const landmarkUniformBuf = device.createBuffer({
    size: SCENE_UNIFORM_SIZE,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    label: 'landmark-uniforms',
  })

  const landmarkInstanceBuf = device.createBuffer({
    size: LANDMARK_INSTANCE_STRIDE * MAX_LANDMARKS,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    label: 'landmark-instances',
  })

  const landmarkBGL = device.createBindGroupLayout({
    label: 'landmark-bgl',
    entries: [
      { binding: 0, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
      { binding: 1, visibility: GPUShaderStage.VERTEX, buffer: { type: 'read-only-storage' } },
    ],
  })

  const landmarkBindGroup = device.createBindGroup({
    layout: landmarkBGL,
    entries: [
      { binding: 0, resource: { buffer: landmarkUniformBuf } },
      { binding: 1, resource: { buffer: landmarkInstanceBuf } },
    ],
    label: 'landmark-bg',
  })

  const landmarkPipeline = device.createRenderPipeline({
    label: 'landmark-pipeline',
    layout: device.createPipelineLayout({ bindGroupLayouts: [landmarkBGL] }),
    vertex: { module: landmarkModule, entryPoint: 'vsMain' },
    fragment: {
      module: landmarkModule,
      entryPoint: 'fsMain',
      targets: [{ format: 'rgba16float', blend: PREMUL_ALPHA_BLEND }],
    },
    primitive: { topology: 'triangle-strip' },
  })

  // ── particles pipeline (Phase 7b) ──────────────────────────────────────────
  const particlesModule = device.createShaderModule({ code: loadShader('particles'), label: 'particles' })

  const particlesUniformBuf = device.createBuffer({
    size: SCENE_UNIFORM_SIZE,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    label: 'particles-uniforms',
  })

  const particlesInstanceBuf = device.createBuffer({
    size: PARTICLE_INSTANCE_STRIDE * MAX_PARTICLES,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    label: 'particles-instances',
  })

  const particlesBGL = device.createBindGroupLayout({
    label: 'particles-bgl',
    entries: [
      { binding: 0, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
      { binding: 1, visibility: GPUShaderStage.VERTEX, buffer: { type: 'read-only-storage' } },
    ],
  })

  const particlesBindGroup = device.createBindGroup({
    layout: particlesBGL,
    entries: [
      { binding: 0, resource: { buffer: particlesUniformBuf } },
      { binding: 1, resource: { buffer: particlesInstanceBuf } },
    ],
    label: 'particles-bg',
  })

  const particlesPipeline = device.createRenderPipeline({
    label: 'particles-pipeline',
    layout: device.createPipelineLayout({ bindGroupLayouts: [particlesBGL] }),
    vertex: { module: particlesModule, entryPoint: 'vsMain' },
    fragment: {
      module: particlesModule,
      entryPoint: 'fsMain',
      targets: [{ format: 'rgba16float', blend: PREMUL_ALPHA_BLEND }],
    },
    primitive: { topology: 'triangle-strip' },
  })

  // ── post-process pipeline ──────────────────────────────────────────────────
  const postModule = device.createShaderModule({ code: loadShader('postprocess'), label: 'postprocess' })

  // Postprocess uniforms: 16 bytes screen + 8 ripple slots × 32 bytes each = 272 bytes.
  // Layout matches the `Uniforms` struct in postprocess.slang (RIPPLE_SLOTS=8).
  const postUniformBuf = device.createBuffer({
    size: 272,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    label: 'post-uniforms',
  })

  const sceneSampler = device.createSampler({
    magFilter: 'linear',
    minFilter: 'linear',
    addressModeU: 'clamp-to-edge',
    addressModeV: 'clamp-to-edge',
  })

  const postBGL = device.createBindGroupLayout({
    label: 'post-bgl',
    entries: [
      { binding: 0, visibility: GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
      { binding: 1, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } },
      { binding: 2, visibility: GPUShaderStage.FRAGMENT, sampler: { type: 'filtering' } },
    ],
  })

  let postBindGroup = buildPostBindGroup(device, postBGL, postUniformBuf, offscreenView, sceneSampler)

  const postPipeline = device.createRenderPipeline({
    label: 'post-pipeline',
    layout: device.createPipelineLayout({ bindGroupLayouts: [postBGL] }),
    vertex: { module: postModule, entryPoint: 'vsMain' },
    fragment: {
      module: postModule,
      entryPoint: 'fsMain',
      targets: [{ format }],
    },
    primitive: { topology: 'triangle-strip' },
  })

  // ── resize handling ────────────────────────────────────────────────────────
  camera.setViewport(renderer.width, renderer.height)
  renderer.onResize((w, h) => {
    camera.setViewport(w, h)
    offscreen.destroy()
    offscreen = createOffscreen(device, w, h)
    offscreenView = offscreen.createView()
    postBindGroup = buildPostBindGroup(device, postBGL, postUniformBuf, offscreenView, sceneSampler)
  })

  // ── per-frame typed arrays ─────────────────────────────────────────────────
  const bgData = new Float32Array(8)        // 32 bytes: float4 camera + float4 misc (used by floaties + hub-ring)
  const wallsUniData = new Float32Array(12) // 48 bytes: camera + misc (time, counts, smin k) + biome wash
  const bgFullData = new Float32Array(48)   // 192 bytes: camera + misc + 8 halos × 16 + washCol + inkCol (background)
  const MAX_HALOS = 8
  const sceneUniData = new Float32Array(24) // 96 bytes: 4×4 matrix + time + pad
  const instanceData = new Float32Array((INSTANCE_STRIDE / 4) * MAX_MICROBES)
  const playerInstData = new Float32Array(MAX_PLAYER_INSTANCES * PLAYER_INSTANCE_STRIDE / 4)
  const chainInstData = new Float32Array(MAX_CHAIN_INSTANCES * CHAIN_INSTANCE_STRIDE / 4)
  const hazardInstData = new Float32Array((HAZARD_INSTANCE_STRIDE / 4) * MAX_HAZARDS)
  const landmarkInstData = new Float32Array((LANDMARK_INSTANCE_STRIDE / 4) * MAX_LANDMARKS)
  const eyesInstData = new Float32Array((EYES_INSTANCE_STRIDE / 4) * MAX_EYES)
  const hyphaeInstData = new Float32Array((HYPHAE_INSTANCE_STRIDE / 4) * MAX_THREADS)
  const particlesInstData = new Float32Array((PARTICLE_INSTANCE_STRIDE / 4) * MAX_PARTICLES)
  const postData = new Float32Array(POST_UNIFORM_SIZE / 4)
  // Reusable scratch matrix for projecting world-space ripple origins to NDC.
  const tmpVp = new Float32Array(16)

  const renderables = world.with('position', 'renderable')
  const chainSegmentEntities = world.with('chainSegment', 'position', 'renderable')
  const hazardEntities = world.with('hazard', 'position', 'renderable')
  const landmarkEntities = world.with('landmark', 'position', 'renderable')
  const eyeEntities = world.with('eyes', 'position', 'renderable')
  const colonyParents = world.with('colony', 'position')
  const colonyMemberEntities = world.with('colonyMember', 'position')
  const particleEntities = world.with('particle', 'position')
  const npcEntities = world.with('npc', 'position', 'renderable')

  const result: RenderSystem = {
    draw(timeSec) {
      const player = playerRef()
      const aspect = renderer.width / renderer.height
      const vp = camera.viewProjection()

      // ── background + floaties uniforms ──
      // floaties + hub-ring use the 32-byte (camera, misc) layout. Background
      // gets the larger layout including halos (Phase 7c).
      bgData[0] = camera.x
      bgData[1] = camera.y
      bgData[2] = camera.effectiveZoom
      bgData[3] = aspect
      bgData[4] = timeSec
      device.queue.writeBuffer(floatiesUniformBuf, 0, bgData)

      // Build pigment halo list — important objects darken/warm the paper
      // around them. Player gets the strongest halo; landmarks + storekeeper
      // a milder one. Cap at MAX_HALOS = 8 (currently fills only 5).
      let haloCount = 0
      function pushHalo(x: number, y: number, radius: number, intensity: number) {
        if (haloCount >= MAX_HALOS) return
        const b = 8 + haloCount * 4
        bgFullData[b + 0] = x
        bgFullData[b + 1] = y
        bgFullData[b + 2] = radius
        bgFullData[b + 3] = intensity
        haloCount++
      }
      // No player halo — a sepia stain trailing the main character read as
      // ominous in playtest. Halos function as world-destination cues; the
      // player is already the camera focus and doesn't need marking.
      for (const lm of landmarkEntities) {
        pushHalo(lm.position.x, lm.position.y, 1.8, 0.85)
      }
      for (const npc of npcEntities) {
        pushHalo(npc.position.x, npc.position.y, 1.4, 0.75)
      }
      // Zero unused halo slots so prior frame data doesn't bleed in.
      for (let i = haloCount; i < MAX_HALOS; i++) {
        const b = 8 + i * 4
        bgFullData[b + 0] = 0
        bgFullData[b + 1] = 0
        bgFullData[b + 2] = 1
        bgFullData[b + 3] = 0
      }
      bgFullData[0] = camera.x
      bgFullData[1] = camera.y
      bgFullData[2] = camera.effectiveZoom
      bgFullData[3] = aspect
      bgFullData[4] = timeSec
      bgFullData[5] = haloCount
      // Biome background: descent strength in misc.z, resolved wash/ink colors
      // in the trailing float4s (offsets 40/44, after the 8 halos).
      {
        const p = playerRef().position
        const bg = biomeBackgroundAt(p.x, p.y)
        bgFullData[6] = bg.strength
        bgFullData[7] = 0
        bgFullData[40] = bg.wash[0]; bgFullData[41] = bg.wash[1]; bgFullData[42] = bg.wash[2]; bgFullData[43] = 0
        bgFullData[44] = bg.ink[0];  bgFullData[45] = bg.ink[1];  bgFullData[46] = bg.ink[2];  bgFullData[47] = 0
      }
      device.queue.writeBuffer(bgUniformBuf, 0, bgFullData)

      // ── dungeon walls uniforms + primitives (viewport-culled) ──
      const dungeon = getActiveDungeon()
      if (dungeon) {
        const cullHalfW = aspect / camera.effectiveZoom + WALL_CULL_MARGIN
        const cullHalfH = 1 / camera.effectiveZoom + WALL_CULL_MARGIN
        const cMinX = camera.x - cullHalfW
        const cMaxX = camera.x + cullHalfW
        const cMinY = camera.y - cullHalfH
        const cMaxY = camera.y + cullHalfH
        let f = 0
        wallsDiscCount = 0
        wallsCapsuleCount = 0
        for (const disc of dungeon.discs) {
          if (disc.x + disc.r < cMinX || disc.x - disc.r > cMaxX ||
              disc.y + disc.r < cMinY || disc.y - disc.r > cMaxY) continue
          if (f + 4 > wallsPrimData.length) break
          wallsPrimData[f++] = disc.x
          wallsPrimData[f++] = disc.y
          wallsPrimData[f++] = disc.r
          wallsPrimData[f++] = 0
          wallsDiscCount++
        }
        for (const c of dungeon.capsules) {
          if (Math.max(c.ax, c.bx) + c.r < cMinX || Math.min(c.ax, c.bx) - c.r > cMaxX ||
              Math.max(c.ay, c.by) + c.r < cMinY || Math.min(c.ay, c.by) - c.r > cMaxY) continue
          if (f + 8 > wallsPrimData.length) break
          wallsPrimData[f++] = c.ax
          wallsPrimData[f++] = c.ay
          wallsPrimData[f++] = c.bx
          wallsPrimData[f++] = c.by
          wallsPrimData[f++] = c.r
          wallsPrimData[f++] = 0
          wallsPrimData[f++] = 0
          wallsPrimData[f++] = 0
          wallsCapsuleCount++
        }
        // Closed doors: plug discs subtracted from the water in the shader,
        // drawn as membrane — w carries the seal kind so each gate reads
        // differently (0 shortcut rose · 1 spore moss · 2 charged · 3
        // official). Open doors simply vanish.
        let wallsPlugCount = 0
        if (dungeon.doors) {
          for (const door of dungeon.doors) {
            if (door.open) continue
            if (door.x + door.r < cMinX || door.x - door.r > cMaxX ||
                door.y + door.r < cMinY || door.y - door.r > cMaxY) continue
            if (f + 4 > wallsPrimData.length) break
            wallsPrimData[f++] = door.x
            wallsPrimData[f++] = door.y
            wallsPrimData[f++] = door.r
            wallsPrimData[f++] =
              door.kind === 'shortcut' ? 0 :
              door.kind === 'lock' ? 3 :
              door.keyId === 'galvanism' ? 2 : 1
            wallsPlugCount++
          }
        }
        if (f > 0) device.queue.writeBuffer(wallsPrimBuf, 0, wallsPrimData.buffer, 0, f * 4)
        wallsUniData[0] = camera.x
        wallsUniData[1] = camera.y
        wallsUniData[2] = camera.effectiveZoom
        wallsUniData[3] = aspect
        wallsUniData[4] = timeSec
        wallsUniData[5] = wallsDiscCount
        wallsUniData[6] = wallsCapsuleCount
        wallsUniData[7] = T.dungeon.sminK
        // Banks descend with the paper: same wash, same strength ramp as
        // background.slang, so bank-vs-water contrast holds in every biome.
        {
          const p = playerRef().position
          const bg = biomeBackgroundAt(p.x, p.y)
          const s = bg.strength * 0.85
          wallsUniData[8] = 1 + (bg.wash[0] - 1) * s
          wallsUniData[9] = 1 + (bg.wash[1] - 1) * s
          wallsUniData[10] = 1 + (bg.wash[2] - 1) * s
          wallsUniData[11] = wallsPlugCount
        }
        device.queue.writeBuffer(wallsUniformBuf, 0, wallsUniData)
      }

      // ── microbe uniforms + instances ──
      sceneUniData.set(vp, 0)
      sceneUniData[16] = timeSec
      device.queue.writeBuffer(creatureUniformBuf, 0, sceneUniData)

      // ── hazard uniforms + instances ──
      device.queue.writeBuffer(hazardUniformBuf, 0, sceneUniData) // vp + timeSec already set
      let hazardCount = 0
      for (const h of hazardEntities) {
        if (hazardCount >= MAX_HAZARDS) break
        const b = hazardCount * (HAZARD_INSTANCE_STRIDE / 4)
        hazardInstData[b + 0] = h.position.x
        hazardInstData[b + 1] = h.position.y
        hazardInstData[b + 2] = h.renderable.radius
        // phase slot doubles as spore lifeFrac (1→0) so the cloud fades out.
        hazardInstData[b + 3] = h.hazard.kind === 'spore' && h.hazard.maxLife
          ? Math.max(0, (h.hazard.life ?? 0) / h.hazard.maxLife)
          : 0
        hazardInstData[b + 4] = h.renderable.tint[0]
        hazardInstData[b + 5] = h.renderable.tint[1]
        hazardInstData[b + 6] = h.renderable.tint[2]
        hazardInstData[b + 7] = HAZARD_SHAPE_ID[h.hazard.kind] ?? 0
        const imp = h.hazard.impulse
        if (imp) {
          const len = Math.sqrt(imp.x * imp.x + imp.y * imp.y)
          hazardInstData[b + 8] = len > 0 ? imp.x / len : 0
          hazardInstData[b + 9] = len > 0 ? imp.y / len : 0
        } else {
          hazardInstData[b + 8] = 0
          hazardInstData[b + 9] = 0
        }
        hazardInstData[b + 10] = 0
        hazardInstData[b + 11] = 0
        hazardCount++
      }
      if (hazardCount > 0) {
        device.queue.writeBuffer(
          hazardInstanceBuf, 0,
          hazardInstData.buffer, 0,
          hazardCount * HAZARD_INSTANCE_STRIDE,
        )
      }

      // ── landmark uniforms + instances ──
      device.queue.writeBuffer(landmarkUniformBuf, 0, sceneUniData)
      let landmarkCount = 0
      for (const lm of landmarkEntities) {
        if (landmarkCount >= MAX_LANDMARKS) break
        const b = landmarkCount * (LANDMARK_INSTANCE_STRIDE / 4)
        landmarkInstData[b + 0] = lm.position.x
        landmarkInstData[b + 1] = lm.position.y
        landmarkInstData[b + 2] = lm.renderable.radius
        landmarkInstData[b + 3] = 0 // phase
        landmarkInstData[b + 4] = lm.renderable.tint[0]
        landmarkInstData[b + 5] = lm.renderable.tint[1]
        landmarkInstData[b + 6] = lm.renderable.tint[2]
        landmarkInstData[b + 7] = LANDMARK_SHAPE_ID[lm.landmark.kind] ?? 0
        landmarkCount++
      }
      if (landmarkCount > 0) {
        device.queue.writeBuffer(
          landmarkInstanceBuf, 0,
          landmarkInstData.buffer, 0,
          landmarkCount * LANDMARK_INSTANCE_STRIDE,
        )
      }

      // ── hub-ring uniforms (same layout as background) ──
      device.queue.writeBuffer(hubRingUniformBuf, 0, bgData)

      let microbeCount = 0
      for (const e of renderables) {
        if (e.player || e.chainSegment || e.hazard || e.landmark) continue
        if (microbeCount >= MAX_MICROBES) break
        const b = microbeCount * (INSTANCE_STRIDE / 4)
        instanceData[b + 0] = e.position.x
        instanceData[b + 1] = e.position.y
        instanceData[b + 2] = e.renderable.radius
        instanceData[b + 3] = e.wobble?.phase ?? 0
        let tr = e.renderable.tint[0]
        let tg = e.renderable.tint[1]
        let tb = e.renderable.tint[2]
        // Charging allies — vivid pop on attack so the lunge reads as
        // "fighting for you" rather than "another blob moving". Applied
        // before health/hit-flash so red HP-tint and white flash still
        // compose normally on top.
        if (e.ally?.state === 'charging') {
          tr = tr + (0.35 - tr) * 0.55
          tg = tg + (1.15 - tg) * 0.55
          tb = tb + (0.55 - tb) * 0.55
        }
        // Ally health: shift tint toward muted red as HP drops
        if (e.ally && e.health && e.health.max > 0) {
          const d = 1 - Math.max(0, e.health.current / e.health.max)
          tr = tr + (0.85 - tr) * d
          tg = tg * (1 - d * 0.85)
          tb = tb * (1 - d * 0.85)
        }
        // Hit flash: brighten toward white on damage
        if (e.hitFlash && e.hitFlash > 0) {
          const ft = Math.min(e.hitFlash / 0.15, 1)
          tr = tr + (1 - tr) * ft * 0.85
          tg = tg + (1 - tg) * ft * 0.85
          tb = tb + (1 - tb) * ft * 0.85
        }
        instanceData[b + 4] = tr
        instanceData[b + 5] = tg
        instanceData[b + 6] = tb
        instanceData[b + 7] = BIOMES[e.renderable.variant ?? 'shallow'].shaderVariant
        instanceData[b + 8] = e.morphology?.featureFlags ?? 0
        instanceData[b + 9] = e.morphology?.seed ?? 0
        instanceData[b + 10] = e.morphology?.featureA ?? 0
        instanceData[b + 11] = e.morphology?.featureB ?? 0
        // Heading for oriented bodies (grain/mito/rod/spiral/plant): swim
        // direction while moving, else a stable angle. Behavior creatures hold
        // a persistent heading (creature-behavior.ts) so they don't spin to the
        // seed-angle between darts.
        {
          const vx = e.velocity?.x ?? 0
          const vy = e.velocity?.y ?? 0
          const pred = e.predator
          // During windup the body is nearly still but the lunge direction is
          // already committed — aim the heading (and so the inhale/bristle)
          // at the target instead of falling back to the seed angle.
          const aimed = pred && (pred.state === 'windup' || pred.state === 'lunge')
          instanceData[b + 12] = aimed
            ? Math.atan2(pred.lungeDir.y, pred.lungeDir.x)
            : e.behavior
              ? e.behavior.heading
              : (vx * vx + vy * vy) > 0.0009
                ? Math.atan2(vy, vx)
                : (e.morphology?.seed ?? 0) * Math.PI * 2
          // Motion integration: normalized swim speed +
          // predator-beat float. Slot 13 saturates around the predator base
          // speed (0.35 chase → ~0.4, 3× lunge → ~0.7). Slot 14: negative =
          // windup progress (bristle telegraph), positive = lunge/recovery
          // envelope (squash + arm drag), 0 = neutral.
          const spd = Math.hypot(vx, vy)
          instanceData[b + 13] = spd / (spd + 0.5)
          let stateF = 0
          if (pred) {
            if (pred.state === 'windup') {
              const dur = pred.windupDuration ?? T.predator.windupDuration
              stateF = -(1 - Math.max(0, pred.stateTimer) / Math.max(dur, 0.001))
            } else if (pred.state === 'lunge') {
              stateF = 1
            } else if (pred.state === 'recovery') {
              stateF = Math.max(0, pred.stateTimer) / T.predator.recoveryDuration
            }
          }
          instanceData[b + 14] = stateF
        }
        // Grammar budget past bit 23: second flags int + extra scalars.
        instanceData[b + 15] = e.morphology?.featureFlags2 ?? 0
        instanceData[b + 16] = e.morphology?.featureC ?? 0
        instanceData[b + 17] = e.morphology?.featureD ?? 0
        instanceData[b + 18] = 0
        instanceData[b + 19] = 0
        microbeCount++
      }
      if (microbeCount > 0) {
        device.queue.writeBuffer(
          creatureInstanceBuf, 0,
          instanceData.buffer, 0,
          microbeCount * INSTANCE_STRIDE,
        )
      }

      // ── chain boss uniforms + instances ──
      device.queue.writeBuffer(chainUniformBuf, 0, sceneUniData) // vp + timeSec already set
      let chainCount = 0
      for (const seg of chainSegmentEntities) {
        if (chainCount >= MAX_CHAIN_INSTANCES) break
        const bossState = seg.chainSegment!.parent.boss?.state
        const awakeFrac = bossState === 'dormant' || bossState === 'uncurling' ? 0 : 1
        const hitFlashFrac = Math.min((seg.hitFlash ?? 0) / 0.18, 1)
        const cb = chainCount * (CHAIN_INSTANCE_STRIDE / 4)
        chainInstData[cb + 0] = seg.position.x
        chainInstData[cb + 1] = seg.position.y
        chainInstData[cb + 2] = seg.renderable.radius
        chainInstData[cb + 3] = timeSec
        chainInstData[cb + 4] = seg.chainSegment!.hueOffset
        chainInstData[cb + 5] = hitFlashFrac
        chainInstData[cb + 6] = awakeFrac
        chainInstData[cb + 7] = 0
        chainCount++
      }
      if (chainCount > 0) {
        device.queue.writeBuffer(
          chainInstanceBuf, 0,
          chainInstData.buffer, 0,
          chainCount * CHAIN_INSTANCE_STRIDE,
        )
      }

      // ── player uniforms + instance ──
      sceneUniData.set(vp, 0)
      sceneUniData[16] = timeSec
      device.queue.writeBuffer(playerUniformBuf, 0, sceneUniData)

      playerInstData[0] = player.position.x
      playerInstData[1] = player.position.y
      playerInstData[2] = player.renderable?.radius ?? 0.12
      playerInstData[3] = player.wobble?.phase ?? 0
      playerInstData[4] = player.velocity?.x ?? 0
      playerInstData[5] = player.velocity?.y ?? 0
      playerInstData[6] = (player.shield && player.shield.activeRemaining > 0) ? 1 : 0
      playerInstData[7] = tierMorphProgress(player)
      // Step 6+: mutation feature flags (bit per kind) and packed stack counts.
      {
        const ms = player.mutations?.stacks ?? {}
        const bits = (k: string, bit: number) => ((ms[k as keyof typeof ms] ?? 0) > 0 ? 1 << bit : 0)
        const shift = (k: string, sh: number) => ((ms[k as keyof typeof ms] ?? 0) & 0xF) << sh
        playerInstData[8] =
          bits('borrowed-teeth', 0) | bits('bouquet', 1) | bits('nucleus-envy', 2) |
          bits('wet-socks', 3) | bits('static-cling', 4) | bits('eyes-everywhere', 5) |
          // bits 8..10: speciated critter form (0 = still an amoeba).
          ((player.lineage?.form ? FORM_ID[player.lineage.form] : 0) << 8)
        playerInstData[9] =
          shift('borrowed-teeth', 0) | shift('bouquet', 4) | shift('nucleus-envy', 8) |
          shift('wet-socks', 12) | shift('static-cling', 16) |
          // bits 20..23: Flagellate level → one rear flagellum per level.
          ((powerLevel(player, 'flagellate') & 0xF) << 20)
      }
      // Slots [10..12]: pre-speciation they carry the toward-Heck flourish
      // ladder (red ink-mix, horns, tail — morph-blended). Once the lineage
      // locks a critter form they are repurposed: [11] = speciation reveal
      // (0→1 ornament grow-in), [12] = form growth (tier 3→6 elaboration).
      // The shader keys the old flourish reads on formId==0 so there is no
      // cross-talk.
      {
        const lin = player.lineage
        if (lin?.form) {
          playerInstData[10] = 0
          playerInstData[11] = formRevealValue(lin)
          playerInstData[12] = formGrowth(player)
        } else {
          const fl = tierFlourish(player)
          playerInstData[10] = fl.red
          playerInstData[11] = fl.horns
          playerInstData[12] = fl.tail
        }
      }
      // V4b treatments: lash whip, osmotic reach halo, engulf gape.
      {
        const pr = player.renderable?.radius ?? 0.12
        const lash = player.lash
        playerInstData[13] = lash ? lash.angle : 0
        playerInstData[14] = lash ? lash.len / Math.max(pr, 0.01) : 0
        playerInstData[15] = lash ? 1 - Math.max(0, lash.timer) / 0.30 : 0
        const reach = player.mods?.eatReachAdd ?? 0
        playerInstData[16] = reach > 0 ? ((pr + reach) / pr) * 0.6 : 0
        playerInstData[17] = player.engulf && player.engulf.activeRemaining > 0
          ? Math.min(1, player.engulf.activeRemaining / 0.35)
          : 0
      }
      // Pack extra player instances (gallery mutation previews)
      const extras = result.extraPlayers
      for (let ei = 0; ei < extras.length && ei < MAX_PLAYER_INSTANCES - 1; ei++) {
        const ep = extras[ei]
        const base = (1 + ei) * (PLAYER_INSTANCE_STRIDE / 4)
        playerInstData[base + 0] = ep.x
        playerInstData[base + 1] = ep.y
        playerInstData[base + 2] = ep.radius
        playerInstData[base + 3] = ep.phase
        playerInstData[base + 4] = 0
        playerInstData[base + 5] = 0
        playerInstData[base + 6] = 0
        // A species-review preview forces its own critter form + growth and
        // renders as a fully-settled body. Otherwise (mutation row) a preview
        // inherits the player's resolved form once speciated, so the gallery
        // shows the body the player actually has; pre-speciation, plain amoeba.
        const form = ep.form ?? player.lineage?.form
        playerInstData[base + 7] = ep.form ? 1 : (ep.tier ?? 0)
        playerInstData[base + 8] = ep.mutationFlags | (form ? FORM_ID[form] << 8 : 0)
        playerInstData[base + 9] = ep.mutationStacks
        for (let z = 10; z <= 17; z++) playerInstData[base + z] = 0
        if (form) {
          playerInstData[base + 11] = 1                                   // fully revealed
          playerInstData[base + 12] = ep.form
            ? (ep.growth ?? 1)
            : growthAt(Math.max(1, Math.min(6, ep.tier ?? 3)) as Tier)
        }
      }
      device.queue.writeBuffer(playerInstanceBuf, 0, playerInstData)

      // ── hyphae threads (mycelial-network edges) ──
      // One strand per surviving edge, endpoints pulled from the live member
      // positions so the web tracks the drifting raft.
      device.queue.writeBuffer(hyphaeUniformBuf, 0, sceneUniData)
      let threadCount = 0
      for (const parent of colonyParents) {
        const col = parent.colony
        if (col.layout !== 'network' || !col.edges) continue
        const bySlot = new Map<number, Entity>()
        for (const m of colonyMemberEntities) {
          if (m.colonyMember.parent === parent) bySlot.set(m.colonyMember.slot, m)
        }
        for (const [sa, sb] of col.edges) {
          if (threadCount >= MAX_THREADS) break
          const ma = bySlot.get(sa), mb = bySlot.get(sb)
          if (!ma || !mb) continue
          const b = threadCount * (HYPHAE_INSTANCE_STRIDE / 4)
          hyphaeInstData[b + 0] = ma.position.x
          hyphaeInstData[b + 1] = ma.position.y
          hyphaeInstData[b + 2] = mb.position.x
          hyphaeInstData[b + 3] = mb.position.y
          hyphaeInstData[b + 4] = 0.038          // strand thickness (world)
          hyphaeInstData[b + 5] = col.agitated ? 0.75 : 1.0
          threadCount++
        }
      }
      if (threadCount > 0) {
        device.queue.writeBuffer(
          hyphaeInstanceBuf, 0,
          hyphaeInstData.buffer, 0,
          threadCount * HYPHAE_INSTANCE_STRIDE,
        )
      }

      // ── eyes uniforms + instances ──
      device.queue.writeBuffer(eyesUniformBuf, 0, sceneUniData)
      let eyeCount = 0
      for (const e of eyeEntities) {
        const bodyR = e.renderable.radius
        const eyes = e.eyes
        for (let s = 0; s < eyes.sockets.length; s++) {
          if (eyeCount >= MAX_EYES) break
          const socket = eyes.sockets[s]
          const pupil = eyes.pupilPos[s]
          const socketWorldR = socket.radius * bodyR
          const pupilR = socketWorldR * 0.45
          const b = eyeCount * (EYES_INSTANCE_STRIDE / 4)
          // Encode eye side in the sign of angryAmount: -1 side = left, +1 = right.
          // abs() recovers angry strength in the shader.
          const side = socket.offset.x < 0 ? -1 : 1
          eyesInstData[b + 0] = e.position.x + socket.offset.x * bodyR
          eyesInstData[b + 1] = e.position.y + socket.offset.y * bodyR
          eyesInstData[b + 2] = socketWorldR
          eyesInstData[b + 3] = pupilR
          eyesInstData[b + 4] = pupil.x
          eyesInstData[b + 5] = pupil.y
          eyesInstData[b + 6] = eyes.blinkAmount
          eyesInstData[b + 7] = eyes.angry * side
          eyeCount++
        }
      }
      if (eyeCount > 0) {
        device.queue.writeBuffer(
          eyesInstanceBuf, 0,
          eyesInstData.buffer, 0,
          eyeCount * EYES_INSTANCE_STRIDE,
        )
      }

      // ── particles uniforms + instances (Phase 7b) ──
      device.queue.writeBuffer(particlesUniformBuf, 0, sceneUniData)
      let particleCount = 0
      for (const e of particleEntities) {
        if (particleCount >= MAX_PARTICLES) break
        const b = particleCount * (PARTICLE_INSTANCE_STRIDE / 4)
        const p = e.particle
        particlesInstData[b + 0] = e.position.x
        particlesInstData[b + 1] = e.position.y
        particlesInstData[b + 2] = p.size
        particlesInstData[b + 3] = Math.min(1, p.age / Math.max(p.lifetime, 0.0001))
        particlesInstData[b + 4] = p.tint[0]
        particlesInstData[b + 5] = p.tint[1]
        particlesInstData[b + 6] = p.tint[2]
        particlesInstData[b + 7] = particleKindId(p.kind)
        particleCount++
      }
      if (particleCount > 0) {
        device.queue.writeBuffer(
          particlesInstanceBuf, 0,
          particlesInstData.buffer, 0,
          particleCount * PARTICLE_INSTANCE_STRIDE,
        )
      }

      // ── post-process uniforms ──
      postData[0] = renderer.width
      postData[1] = renderer.height
      postData[2] = timeSec
      // w = active ripple count (read by postprocess.slang loop bound).
      const ripples = activeRipples()
      const rippleCount = Math.min(ripples.length, MAX_RIPPLES)
      postData[3] = rippleCount
      // Project each ripple origin (world space) to NDC using the camera's
      // view-projection matrix. Same matrix as the scene draws so ripples line
      // up with what's on screen.
      tmpVp.set(vp)
      for (let ri = 0; ri < MAX_RIPPLES; ri++) {
        const base = 4 + ri * 8
        if (ri < rippleCount) {
          const r = ripples[ri]
          // 4×4 column-major: NDC = vp * [x, y, 0, 1].
          const wx = r.x, wy = r.y
          const ndcX = tmpVp[0] * wx + tmpVp[4] * wy + tmpVp[12]
          const ndcY = tmpVp[1] * wx + tmpVp[5] * wy + tmpVp[13]
          const ndcW = tmpVp[3] * wx + tmpVp[7] * wy + tmpVp[15]
          const invW = ndcW !== 0 ? 1 / ndcW : 1
          postData[base + 0] = ndcX * invW
          postData[base + 1] = ndcY * invW
          postData[base + 2] = r.age
          postData[base + 3] = r.lifetime
          postData[base + 4] = r.strength
          postData[base + 5] = r.glitch ? 1 : 0
          postData[base + 6] = 0
          postData[base + 7] = 0
        } else {
          // Zero out unused slots so stale ripples from prior frames don't bleed in.
          postData[base + 0] = 0
          postData[base + 1] = 0
          postData[base + 2] = 0
          postData[base + 3] = 1
          postData[base + 4] = 0
          postData[base + 5] = 0
          postData[base + 6] = 0
          postData[base + 7] = 0
        }
      }
      device.queue.writeBuffer(postUniformBuf, 0, postData)

      // ── encode ────────────────────────────────────────────────────────────
      const encoder = device.createCommandEncoder()

      // Scene pass: background + microbes + player → offscreen HDR texture
      const scenePass = encoder.beginRenderPass({
        colorAttachments: [{
          view: offscreenView,
          loadOp: 'clear',
          storeOp: 'store',
          clearValue: { r: 0, g: 0, b: 0, a: 1 },
        }],
      })

      scenePass.setPipeline(bgPipeline)
      scenePass.setBindGroup(0, bgBindGroup)
      scenePass.draw(4)

      // Dungeon walls — over the paper, under everything that swims.
      if (dungeon && wallsDiscCount + wallsCapsuleCount > 0) {
        scenePass.setPipeline(wallsPipeline)
        scenePass.setBindGroup(0, wallsBindGroup)
        scenePass.draw(4)
      }

      // Hub boundary ring — drawn just above background
      scenePass.setPipeline(hubRingPipeline)
      scenePass.setBindGroup(0, hubRingBindGroup)
      scenePass.draw(4)

      // Landmarks — drawn under hazards
      if (landmarkCount > 0) {
        scenePass.setPipeline(landmarkPipeline)
        scenePass.setBindGroup(0, landmarkBindGroup)
        scenePass.draw(4, landmarkCount)
      }

      if (hazardCount > 0) {
        scenePass.setPipeline(hazardPipeline)
        scenePass.setBindGroup(0, hazardBindGroup)
        scenePass.draw(4, hazardCount)
      }

      // Hyphae threads — under the mushroom node bodies so the caps sit on the
      // strand ends (the web reads as rooted, not floating on top).
      if (threadCount > 0) {
        scenePass.setPipeline(hyphaePipeline)
        scenePass.setBindGroup(0, hyphaeBindGroup)
        scenePass.draw(4, threadCount)
      }

      if (microbeCount > 0) {
        scenePass.setPipeline(creaturePipeline)
        scenePass.setBindGroup(0, creatureBindGroup)
        scenePass.draw(4, microbeCount)
      }

      if (chainCount > 0) {
        scenePass.setPipeline(chainPipeline)
        scenePass.setBindGroup(0, chainBindGroup)
        scenePass.draw(4, chainCount)
      }

      scenePass.setPipeline(playerPipeline)
      scenePass.setBindGroup(0, playerBindGroup)
      scenePass.draw(4, 1 + Math.min(result.extraPlayers.length, MAX_PLAYER_INSTANCES - 1))

      // Eyes — drawn on top of every creature body.
      if (eyeCount > 0) {
        scenePass.setPipeline(eyesPipeline)
        scenePass.setBindGroup(0, eyesBindGroup)
        scenePass.draw(4, eyeCount)
      }

      // Particles — pigment puffs / ink splatters / spore clusters. Drawn
      // above creatures + eyes so they read as paint thrown over the world.
      if (particleCount > 0) {
        scenePass.setPipeline(particlesPipeline)
        scenePass.setBindGroup(0, particlesBindGroup)
        scenePass.draw(4, particleCount)
      }

      // Foreground parallax floaties — drawn in front of all creatures.
      scenePass.setPipeline(floatiesPipeline)
      scenePass.setBindGroup(0, floatiesBindGroup)
      scenePass.draw(4)

      scenePass.end()

      // Post-process pass: offscreen texture → canvas
      const postPass = encoder.beginRenderPass({
        colorAttachments: [{
          view: renderer.context.getCurrentTexture().createView(),
          loadOp: 'clear',
          storeOp: 'store',
          clearValue: { r: 0, g: 0, b: 0, a: 1 },
        }],
      })

      postPass.setPipeline(postPipeline)
      postPass.setBindGroup(0, postBindGroup)
      postPass.draw(4)

      postPass.end()

      device.queue.submit([encoder.finish()])
    },
    extraPlayers: [],
  }
  return result
}
